import { useCallback, useMemo } from "react"

import {
  selectBorrowingFeeRate,
  selectCollateralPrice,
  selectGasCompensation,
  selectMinimumCollateralRatioInCurrentMode,
  selectTotalMats,
  selectWalletBalances,
  useDappSelector,
} from "#/redux-state"
import {
  bigIntToHumanReadableFormat,
  separateThousandsWithComma,
} from "#/shared/utils"

import { useBorrowingFee } from "./borrow-system"

// This is manually set to the safe buffer value for the borrow transaction.
export const GAS_BUFFER_FOR_BORROW_TRANSACTION = 5n * 10n ** 12n
export const GAS_BUFFER_FOR_BORROW_UPDATE_TRANSACTION = 1n * 10n ** 12n
export const GAS_BUFFER_FOR_BORROW_REPAY_TRANSACTION = 1n * 10n ** 12n
export const GAS_BUFFER_FOR_BORROW_COLLATERAL_TRANSACTION = 1n * 10n ** 12n

export const useCalculateCollateralizationRatio = () => {
  const collateralPrice = useDappSelector(selectCollateralPrice)

  const calculateCollateralizationRatio = useCallback(
    (loanAmount: bigint, collateralAmount: bigint) => {
      if (!collateralPrice || !loanAmount || !collateralAmount) return 0n

      const collateralValue = collateralPrice * collateralAmount

      return collateralValue / loanAmount
    },
    [collateralPrice],
  )

  return { calculateCollateralizationRatio }
}

export const useCalculateCollateralAmountBasedOnRatio = () => {
  const collateralPrice = useDappSelector(selectCollateralPrice)

  const calculateCollateralAmountBasedOnRatio = useCallback(
    (loanAmount: bigint, collateralizationRatio: bigint) => {
      if (!collateralPrice) return 0n

      const collateralValue = (loanAmount * collateralizationRatio) / 10n ** 18n

      return (collateralValue * 10n ** 18n) / collateralPrice
    },
    [collateralPrice],
  )

  return { calculateCollateralAmountBasedOnRatio }
}

export const useCollateralizationRatioForExistingLoan = (
  existingLoanAmount: bigint,
  addedLoanAmount: bigint,
  existingCollateralAmount: bigint,
  addedCollateralAmount: bigint,
) => {
  const { calculateBorrowingFee } = useBorrowingFee()

  const { calculateCollateralizationRatio } =
    useCalculateCollateralizationRatio()

  const loanWithFeesValue = useMemo(() => {
    if (!addedLoanAmount) return existingLoanAmount

    return (
      existingLoanAmount +
      addedLoanAmount +
      calculateBorrowingFee(addedLoanAmount)
    )
  }, [existingLoanAmount, addedLoanAmount, calculateBorrowingFee])

  const collateralizationRatio = useMemo(
    () =>
      calculateCollateralizationRatio(
        loanWithFeesValue,
        existingCollateralAmount + addedCollateralAmount,
      ),
    [
      calculateCollateralizationRatio,
      existingCollateralAmount,
      addedCollateralAmount,
      loanWithFeesValue,
    ],
  )

  const collateralizationRatioLabel = useMemo(
    () =>
      separateThousandsWithComma(
        bigIntToHumanReadableFormat((collateralizationRatio ?? 0n) * 100n),
        0,
        2,
      ),
    [collateralizationRatio],
  )

  return {
    collateralizationRatio,
    collateralizationRatioLabel,
    loanWithFeesValue,
  }
}

export const useMaxCollateralBalance = () => {
  const { mBTC } = useDappSelector(selectWalletBalances)

  const maxCollateralBalance = useMemo(
    () => mBTC.balance - GAS_BUFFER_FOR_BORROW_TRANSACTION,
    [mBTC.balance],
  )

  return { maxCollateralBalance }
}

export const useMaxLoanAmount = () => {
  const collateralPrice = useDappSelector(selectCollateralPrice)
  const gasCompensation = useDappSelector(selectGasCompensation)
  const borrowingFeeRate = useDappSelector(selectBorrowingFeeRate)

  const minimumCollateralRatio = useDappSelector(
    selectMinimumCollateralRatioInCurrentMode,
  )

  const { maxCollateralBalance } = useMaxCollateralBalance()

  const { maxEligibleLoanAmount, maxCollateralAmount } = useMemo(() => {
    if (
      !minimumCollateralRatio ||
      !gasCompensation ||
      !collateralPrice ||
      !borrowingFeeRate
    )
      return { maxEligibleLoanAmount: 0n, maxCollateralAmount: 0n }

    const collateralValue = maxCollateralBalance * collateralPrice

    // loan = (collateralValue - collateral_ratio * gas) / (collateral_ratio * (1 + borrowing_rate))
    const maxLoanValue =
      ((collateralValue - minimumCollateralRatio * gasCompensation) *
        10n ** 18n) /
      (minimumCollateralRatio * (borrowingFeeRate + 10n ** 18n))

    return {
      maxEligibleLoanAmount: maxLoanValue,
      maxCollateralAmount: collateralValue,
    }
  }, [
    minimumCollateralRatio,
    gasCompensation,
    collateralPrice,
    borrowingFeeRate,
    maxCollateralBalance,
  ])

  const maxEligibleLoanAmountLabel = useMemo(
    () =>
      maxEligibleLoanAmount && maxEligibleLoanAmount > 0n
        ? separateThousandsWithComma(
            bigIntToHumanReadableFormat(maxEligibleLoanAmount),
            2,
          )
        : "0",
    [maxEligibleLoanAmount],
  )

  const maxEligibleCollateralAmountLabel = useMemo(
    () =>
      maxCollateralAmount
        ? separateThousandsWithComma(
            bigIntToHumanReadableFormat(maxCollateralAmount),
            2,
          )
        : "0",
    [maxCollateralAmount],
  )

  return {
    maxCollateralBalance,
    maxEligibleLoanAmount,
    maxEligibleLoanAmountLabel,
    maxEligibleCollateralAmountLabel,
  }
}

export const useMinimumRequiredCollateral = (loanAmount: bigint) => {
  const collateralPrice = useDappSelector(selectCollateralPrice)

  const minimumCollateralRatio = useDappSelector(
    selectMinimumCollateralRatioInCurrentMode,
  )

  const requiredCollateralValue = useMemo(
    () =>
      loanAmount && minimumCollateralRatio
        ? (loanAmount * minimumCollateralRatio) / 10n ** 18n
        : null,
    [loanAmount, minimumCollateralRatio],
  )

  const requiredCollateralAmount = useMemo(
    () =>
      collateralPrice && requiredCollateralValue
        ? (requiredCollateralValue * 10n ** 18n) / collateralPrice
        : 0n,
    [collateralPrice, requiredCollateralValue],
  )

  return { requiredCollateralAmount }
}

export const useMaxBorrowAmount = () => {
  // TODO: check max borrow amount against the value from the contract
  const collateralPrice = useDappSelector(selectCollateralPrice)
  const gasCompensation = useDappSelector(selectGasCompensation)
  const totalMats = useDappSelector(selectTotalMats)

  const minimumCollateralRatio = useDappSelector(
    selectMinimumCollateralRatioInCurrentMode,
  )

  const { maxCollateralBalance } = useMaxCollateralBalance()
  const { calculateBorrowingFee } = useBorrowingFee()

  // Potential balance of mBTC after all available mats are converted.
  // This is the value if user converts all available mats into sats.
  // The conversion rate is 1 mats = 10 sats.
  // We also need to normalize the decimals to be able to correctly
  // sum if with current mBTC balance
  const potentialMbtcBalance =
    BigInt(totalMats) * 10n ** 10n + maxCollateralBalance

  const calculateCredit = useCallback(
    (amount: bigint) => {
      if (!minimumCollateralRatio || !gasCompensation || !collateralPrice)
        return null
      const collateralValue = amount * collateralPrice

      const rawLoanAmount =
        collateralValue / minimumCollateralRatio - gasCompensation

      return rawLoanAmount - calculateBorrowingFee(rawLoanAmount)
    },
    [
      calculateBorrowingFee,
      collateralPrice,
      gasCompensation,
      minimumCollateralRatio,
    ],
  )

  const maxEligibleCredit = useMemo(
    () => calculateCredit(maxCollateralBalance),
    [maxCollateralBalance, calculateCredit],
  )

  const maxEligibleCreditLabel = useMemo(
    () =>
      maxEligibleCredit
        ? separateThousandsWithComma(
            bigIntToHumanReadableFormat(maxEligibleCredit),
            2,
          )
        : "0",
    [maxEligibleCredit],
  )

  const potentialMaxEligibleCredit = useMemo(
    () => calculateCredit(potentialMbtcBalance),
    [potentialMbtcBalance, calculateCredit],
  )

  return {
    maxEligibleCredit,
    maxEligibleCreditLabel,
    potentialMaxEligibleCredit,
    potentialMbtcBalance,
  }
}
